/**
 * AmountDisplay Component
 *
 * Currency-aware amount formatting for displaying monetary values.
 * Uses Intl.NumberFormat for locale-aware formatting.
 *
 * @see types/accounting.ts for related types
 */

import clsx from 'clsx';

export interface AmountDisplayProps {
  /** Amount in cents (e.g., 4700 = 47.00) */
  amountCents: number;
  /** Currency code (EUR, USD, GBP, etc.) */
  currency: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show + sign for positive amounts */
  showSign?: boolean;
  /** Whether this is an income transaction (displays in green) */
  isIncome?: boolean;
}

/**
 * Currency configuration for locale-aware formatting
 */
const CURRENCY_CONFIG: Record<string, { locale: string; symbol: string }> = {
  EUR: { locale: 'de-DE', symbol: '€' },
  USD: { locale: 'en-US', symbol: '$' },
  GBP: { locale: 'en-GB', symbol: '£' },
};

/**
 * Formats an amount in cents to a currency string
 */
export function formatAmount(
  amountCents: number,
  currency: string,
  showSign = false
): string {
  const config = CURRENCY_CONFIG[currency] || { locale: 'de-DE', symbol: currency };
  const amount = amountCents / 100;

  const formatter = new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  let formatted = formatter.format(Math.abs(amount));

  // Handle sign display
  if (amount < 0) {
    formatted = `-${formatted}`;
  } else if (showSign && amount > 0) {
    formatted = `+${formatted}`;
  }

  return formatted;
}

/**
 * AmountDisplay renders a monetary amount with proper formatting.
 *
 * Features:
 * - Locale-aware currency formatting (EUR uses German format, USD uses US format)
 * - Color coding for positive/negative amounts
 * - Optional sign display for positive amounts
 *
 * @example
 * <AmountDisplay amountCents={4700} currency="EUR" />
 * // Renders: €47,00
 *
 * @example
 * <AmountDisplay amountCents={-12000} currency="USD" showSign />
 * // Renders: -$120.00 (in red)
 */
export function AmountDisplay({
  amountCents,
  currency,
  className,
  showSign = false,
  isIncome = false,
}: AmountDisplayProps) {
  const isNegative = amountCents < 0;
  const formattedAmount = formatAmount(amountCents, currency, showSign);

  return (
    <span
      className={clsx(
        'font-mono tabular-nums',
        {
          'text-green-600': isIncome,
          'text-red-600': !isIncome && isNegative,
          'text-gray-900': !isIncome && !isNegative,
        },
        className
      )}
      data-testid="amount-display"
      aria-label={`${formattedAmount}`}
    >
      {formattedAmount}
    </span>
  );
}

export default AmountDisplay;
